import { ReloadOutlined } from '@ant-design/icons';
import type { WaveOrderRow, WaveRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Empty, Input, Row, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const SUPPLY_STATUS: Record<string, string> = {
  NEW: '待接单',
  ACCEPTED: '已接单',
  PICKING: '拣货中',
  PICKED: '已拣货',
  SHIPPED: '已发运',
  ARRIVED: '已到达',
  SIGNED: '已签收',
  CANCELED: '已取消'
};

/** 波次作业：未编波指令合并成波次，供外仓合并拣货/发运。 */
export default function WaveOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [waveNo, setWaveNo] = useState('');
  const [activeWave, setActiveWave] = useState<string | null>(null);

  const waves = useQuery({ queryKey: ['ops', 'wave'], queryFn: () => hq.opsExtras.wave.list() });
  const unassigned = useQuery({
    queryKey: ['ops', 'wave', 'unassigned'],
    queryFn: () => hq.opsExtras.wave.orders({ unassigned: true })
  });
  const waveOrders = useQuery({
    queryKey: ['ops', 'wave', 'orders', activeWave],
    enabled: Boolean(activeWave),
    queryFn: () => hq.opsExtras.wave.orders({ waveNo: activeWave ?? '' })
  });

  const assign = useMutation({
    mutationFn: () => hq.opsExtras.wave.assign({ orderIds: selected, waveNo: waveNo.trim() || undefined }),
    onSuccess: async (result) => {
      message.success(`已编入波次 ${result.waveNo}（${result.assigned} 单）`);
      setSelected([]);
      setWaveNo('');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'wave'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '编波失败')
  });

  const columns: ColumnsType<WaveOrderRow> = [
    { title: '指令号', dataIndex: 'bizNo', width: 170 },
    { title: '类型', dataIndex: 'triggerType', width: 130, render: (value: string) => <Tag>{value}</Tag> },
    { title: '状态', dataIndex: 'supplyStatus', width: 110, render: (value: string) => <Tag color="blue">{SUPPLY_STATUS[value] ?? value}</Tag> },
    { title: '拆单序', dataIndex: 'splitNo', width: 90, align: 'right', render: (value: number) => (value > 0 ? `#${value}` : '-') },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '应急', dataIndex: 'isEmergency', width: 80, render: (value: boolean) => (value ? <Tag color="red">应急</Tag> : '-') },
    { title: 'SLA', dataIndex: 'slaDeadline', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    { title: '通知时间', dataIndex: 'notifyAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  return (
    <Row gutter={16}>
      <Col span={9}>
        <Card
          className="page-card"
          title={<Space>未编波指令<Typography.Text type="secondary">{unassigned.data?.length ?? 0} 单</Typography.Text></Space>}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => unassigned.refetch()} loading={unassigned.isFetching}>
              刷新
            </Button>
          }
        >
          {unassigned.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="加载失败"
              description={unassigned.error instanceof ApiError ? unassigned.error.friendlyMessage : ''}
            />
          ) : null}
          <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
            <Input placeholder="波次号（留空自动生成）" value={waveNo} onChange={(event) => setWaveNo(event.target.value)} maxLength={32} />
            <Button type="primary" disabled={selected.length === 0} loading={assign.isPending} onClick={() => assign.mutate()}>
              编波（{selected.length}）
            </Button>
          </Space.Compact>
          <Table<WaveOrderRow>
            rowKey="id"
            size="small"
            columns={columns.slice(0, 6)}
            dataSource={unassigned.data ?? []}
            loading={unassigned.isPending}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
            scroll={{ x: 720 }}
            locale={{ emptyText: '没有待编波指令' }}
          />
        </Card>
      </Col>
      <Col span={15}>
        <Card
          className="page-card"
          title={<Space>波次列表<Typography.Text type="secondary">{waves.data?.length ?? 0} 个</Typography.Text></Space>}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => waves.refetch()} loading={waves.isFetching}>
              刷新
            </Button>
          }
        >
          <Table<WaveRow>
            rowKey="waveNo"
            size="small"
            columns={[
              { title: '波次号', dataIndex: 'waveNo', width: 200 },
              { title: '指令数', dataIndex: 'orderCount', width: 90, align: 'right' },
              { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
              { title: '应急', dataIndex: 'emergencyCount', width: 90, align: 'right', render: (value: number) => (value > 0 ? <Tag color="red">{value}</Tag> : '-') },
              {
                title: '最早 SLA',
                dataIndex: 'earliestSla',
                width: 160,
                render: (value: string | null) => formatDisplayTime(value)
              },
              { title: '状态分布', dataIndex: 'statusSummary', render: (value: string) => value || '-' }
            ]}
            dataSource={waves.data ?? []}
            loading={waves.isPending}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            onRow={(row) => ({ onClick: () => setActiveWave(row.waveNo), style: { cursor: 'pointer' } })}
            rowClassName={(row) => (row.waveNo === activeWave ? 'ant-table-row-selected' : '')}
            scroll={{ x: 900 }}
            locale={{ emptyText: '还没有波次' }}
          />

          <Typography.Title level={5} style={{ marginTop: 16 }}>
            {activeWave ? `波次 ${activeWave} 明细` : '选择上方波次查看明细'}
          </Typography.Title>
          {activeWave ? (
            <Table<WaveOrderRow>
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={waveOrders.data ?? []}
              loading={Boolean(activeWave) && waveOrders.isPending}
              pagination={false}
              scroll={{ x: sumWidth(columns) }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未选择波次" />
          )}
        </Card>
      </Col>
    </Row>
  );
}
